/**
 * Outil « Transformations sur quadrillage » (Boîte à outils, 6e → 3e).
 *
 * Observer : une figure (drapeau, triangle, équerre) dont on déplace les
 * sommets, et son image par la symétrie axiale (6e : axe vertical,
 * horizontal ou diagonal qu'on fait glisser), le demi-tour de centre O (5e)
 * ou la translation qui fait glisser selon une flèche (4e ; 3e : le
 * parallélogramme AA′B′B). « Construction » montre comment on obtient A′.
 * Construire : l'image est cachée, on place A′, B′… au doigt (ou au
 * clavier) ; chaque erreur reçoit une phrase qui dit quoi corriger.
 * Calculs : `transform.ts`.
 */

import { useRef, useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { cn } from '@jeraisonne/ui';
import type { ToolProps } from '../../tool-types';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented, ToggleChip } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { makeFrame, snapPoint, type Viewport } from '../../ui/frame';
import {
  DragHandle,
  FIGURE_BG_CLASS,
  HaloText,
  INK,
  PlotGrid,
  RightAngleMark,
  VectorArrow,
} from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  area,
  axisHandle,
  axisSegment,
  axisThrough,
  feedback,
  isSimple,
  footOnAxis,
  imageOf,
  LETTERS,
  orientation,
  PRESETS,
  properties,
  ptText,
  samePt,
  type AxisDir,
  type Kind,
  type Params,
  type Pt,
} from './transform';

const SIZE = 340;
const M = 7;
const VP: Viewport = { xMin: -M, xMax: M, yMin: -M, yMax: M };
const frame = makeFrame(VP, SIZE);
const C_FIG = 'var(--color-primary-500)';
const C_IMG = 'var(--color-accent-500)';
const C_TOOL = 'var(--color-success-600)';
const C_BAD = 'var(--color-error-500)';

const KIND_LABEL: Record<Kind, string> = {
  axial: 'Symétrie axiale',
  central: 'Demi-tour',
  translation: 'Translation',
};

const AXIS_DIRS: Array<{ value: AxisDir; label: string; name: string }> = [
  { value: 'v', label: '|', name: 'Axe vertical' },
  { value: 'h', label: '—', name: 'Axe horizontal' },
  { value: 'd1', label: '/', name: 'Axe diagonal montant' },
  { value: 'd2', label: '\\', name: 'Axe diagonal descendant' },
];

type Handle = `v${number}` | 'axis' | 'O' | 'U' | 'V' | 'cursor';

const px = (p: Pt) => ({ x: frame.tx(p.x), y: frame.ty(p.y) });
const inView = (p: Pt) => Math.abs(p.x) <= M && Math.abs(p.y) <= M;
const clampAxisPoint = (p: Pt): Pt => ({
  x: Math.max(-M, Math.min(M, p.x)),
  y: Math.max(-M, Math.min(M, p.y)),
});
/** « 1 carreau », « 8 carreaux », « 0,5 carreau » */
const squares = (v: number) => `${String(v).replace('.', ',')} carreau${v >= 2 ? 'x' : ''}`;

const DEFAULT_PARAMS: Params = {
  axis: { dir: 'v', c: 0 },
  center: { x: 0, y: 0 },
  from: { x: 1, y: -6 },
  to: { x: 5, y: -5 },
};

export function TransformationsTool({ level }: ToolProps & { level?: string | null }) {
  const rank = Math.min(4, Math.max(1, schoolLevelRank(level) ?? 4));
  const kinds: Kind[] = (['axial', 'central', 'translation'] as const).slice(0, Math.min(3, rank));
  const [pickedKind, setKind] = useState<Kind>(kinds.at(-1)!);
  const kind = kinds.includes(pickedKind) ? pickedKind : kinds.at(-1)!;
  const [mode, setMode] = useState<'see' | 'build'>('see');
  const [presetId, setPresetId] = useState('drapeau');
  const [pts, setPts] = useState<Pt[]>(PRESETS[0]!.pts);
  const [q, setQ] = useState<Params>(DEFAULT_PARAMS);
  const [construct, setConstruct] = useState(false);
  // Construire
  const [placed, setPlaced] = useState<Pt[]>([]);
  const [wrong, setWrong] = useState<{ p: Pt; say: string } | null>(null);
  const [cursor, setCursor] = useState<Pt>({ x: 0, y: 0 });
  const svgRef = useRef<SVGSVGElement>(null);

  const images = pts.map((p) => imageOf(kind, p, q));
  const building = mode === 'build';
  const k = placed.length;
  const done = building && k === pts.length;

  const resetBuild = () => {
    setPlaced([]);
    setWrong(null);
  };
  // l'image doit rester dans le quadrillage (sinon Construire serait impossible) et la figure rester simple
  const fits = (k2: Kind, list: Pt[], params: Params) =>
    list.every((p) => inView(imageOf(k2, p, params)));
  const setParams = (next: Params) => {
    if (!fits(kind, pts, next)) return;
    setQ(next);
    resetBuild();
  };

  const place = (g: Pt) => {
    if (done) return;
    const p = pts[k]!;
    const say = feedback(kind, p, g, q, LETTERS[k]!);
    if (say === null) {
      setPlaced((old) => [...old, g]);
      setWrong(null);
    } else setWrong({ p: g, say });
  };

  const toolIds: Handle[] = kind === 'axial' ? ['axis'] : kind === 'central' ? ['O'] : ['U', 'V'];
  const ids: Handle[] = building
    ? ['cursor']
    : [...pts.map((_, i) => `v${i}` as Handle), ...toolIds];

  const pointOf = (h: Handle): Pt => {
    if (h === 'axis') return axisHandle(q.axis);
    if (h === 'O') return q.center;
    if (h === 'U') return q.from;
    if (h === 'V') return q.to;
    if (h === 'cursor') return cursor;
    return pts[Number(h.slice(1))]!;
  };

  const drag = useDrag<Handle>({
    svgRef,
    frame,
    ids,
    keyStep: 1,
    grabRadius: building ? 0 : 26,
    pointOf,
    onDrag: (h, p) => {
      if (h === 'cursor') return setCursor(snapPoint(p, VP, 1));
      if (h === 'axis') {
        const axis = axisThrough(q.axis.dir, snapPoint(p, VP, 1));
        // poignée (0 ; c) des diagonales : toujours dans le cadre
        if (Math.abs(axis.c) > M) return;
        return setParams({ ...q, axis });
      }
      if (h === 'O') return setParams({ ...q, center: snapPoint(p, VP, 0.5) });
      if (h === 'U' || h === 'V') {
        const s = snapPoint(p, VP, 1);
        const next = h === 'U' ? { ...q, from: s } : { ...q, to: s };
        if (!samePt(next.from, next.to)) setParams(next);
        return;
      }
      const i = Number(h.slice(1));
      const s = snapPoint(p, VP, 1);
      if (pts.some((o, j) => j !== i && samePt(o, s))) return;
      const next = pts.map((o, j) => (j === i ? s : o));
      if (!isSimple(next) || !fits(kind, next, q)) return;
      setPts(next);
      resetBuild();
    },
    onBackground: building
      ? (p, phase) => {
          const s = snapPoint(p, VP, 1);
          setCursor(s);
          if (phase === 'start') place(s);
        }
      : undefined,
  });

  const choosePreset = (id: string) => {
    setPresetId(id);
    const next = PRESETS.find((p) => p.id === id)!.pts;
    setPts(next);
    if (!fits(kind, next, q)) setQ(DEFAULT_PARAMS);
    resetBuild();
  };

  const outside = images.some((p) => !inView(p));
  // sans la vue : la figure, l'outil, et ce qui est déjà placé
  const describe = () => {
    const tool =
      kind === 'axial'
        ? `axe ${q.axis.dir === 'v' ? `vertical x = ${q.axis.c}` : q.axis.dir === 'h' ? `horizontal y = ${q.axis.c}` : q.axis.dir === 'd1' ? `diagonal y = x + ${q.axis.c}` : `diagonal y = −x + ${q.axis.c}`}`
        : kind === 'central'
          ? `centre O ${ptText(q.center)}`
          : `flèche de ${ptText(q.from)} à ${ptText(q.to)}`;
    const placedText = placed.map((p, i) => `${LETTERS[i]}′ ${ptText(p)}`).join(', ');
    return `Sommets : ${pts.map((p, i) => `${LETTERS[i]} ${ptText(p)}`).join(', ')}. ${tool}.${placedText ? ` Déjà placés : ${placedText}.` : ''}`;
  };
  const poly = (list: Pt[]) => list.map((p) => `${px(p).x},${px(p).y}`).join(' ');
  const [ax1, ax2] = kind === 'axial' ? axisSegment(q.axis, M + 1) : [null, null];
  const a0 = pts[0]!;
  const a0i = images[0]!;
  const b0 = pts[1]!;
  const b0i = images[1]!;

  const shownImages = building ? placed : images;

  return (
    <div className="space-y-4">
      {kinds.length > 1 && (
        <Segmented
          ariaLabel="Transformation"
          value={kind}
          options={kinds.map((v) => ({ value: v, label: KIND_LABEL[v] }))}
          onChange={(v) => {
            setKind(v);
            if (!fits(v, pts, q)) setQ(DEFAULT_PARAMS);
            resetBuild();
          }}
        />
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          ariaLabel="Mode"
          value={mode}
          options={[
            { value: 'see', label: 'Observer' },
            { value: 'build', label: 'Construire' },
          ]}
          onChange={(v) => {
            setMode(v);
            resetBuild();
          }}
        />
        <label className="min-w-0 flex-1">
          <span className="sr-only">Figure</span>
          <select
            value={presetId}
            onChange={(e) => choosePreset(e.target.value)}
            className="border-ink-200 text-ink-900 min-h-11 w-full rounded-xl border bg-transparent px-2 text-sm"
          >
            {PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {kind === 'axial' && (
        <div role="group" aria-label="Direction de l’axe" className="flex flex-wrap gap-2">
          {AXIS_DIRS.map((d) => (
            <ToggleChip
              key={d.value}
              on={q.axis.dir === d.value}
              onClick={() =>
                setParams({ ...q, axis: axisThrough(d.value, clampAxisPoint(axisHandle(q.axis))) })
              }
            >
              <span aria-hidden className="inline-block w-6 font-mono text-base">
                {d.label}
              </span>
              <span className="sr-only">{d.name}</span>
            </ToggleChip>
          ))}
        </div>
      )}

      <div className="mx-auto w-full max-w-[24rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="group"
          aria-label={`${KIND_LABEL[kind]} d’une figure de ${pts.length} sommets sur un quadrillage`}
          data-testid="tr-figure"
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} step={1} labels={false} axes={false} />
          {/* outil de la transformation */}
          {kind === 'axial' && ax1 && ax2 && (
            <line
              x1={px(ax1).x}
              y1={px(ax1).y}
              x2={px(ax2).x}
              y2={px(ax2).y}
              stroke={C_TOOL}
              strokeWidth={3}
            />
          )}
          {kind === 'translation' && (
            <VectorArrow
              x1={px(q.from).x}
              y1={px(q.from).y}
              x2={px(q.to).x}
              y2={px(q.to).y}
              color={C_TOOL}
              width={4}
            />
          )}
          {/* figure */}
          <polygon
            points={poly(pts)}
            fill={C_FIG}
            fillOpacity={0.15}
            stroke={C_FIG}
            strokeWidth={2.5}
            strokeLinejoin="round"
          />
          {/* image */}
          {(!building || done) && (
            <polygon
              points={poly(images)}
              fill={C_IMG}
              fillOpacity={0.18}
              stroke={C_IMG}
              strokeWidth={2.5}
              strokeLinejoin="round"
              strokeDasharray={building ? undefined : '0'}
              data-testid="tr-image"
            />
          )}
          {building && !done && placed.length > 1 && (
            <polyline
              points={poly(placed)}
              fill="none"
              stroke={C_IMG}
              strokeWidth={2.5}
              strokeLinejoin="round"
            />
          )}
          {/* construction du point A′ */}
          {!building && construct && (
            <Construction kind={kind} a={a0} ai={a0i} b={b0} bi={b0i} q={q} rank={rank} />
          )}
          {shownImages.map((p, i) => (
            <g key={`i${i}`}>
              <circle cx={px(p).x} cy={px(p).y} r={4.5} fill={C_IMG} />
              <HaloText x={px(p).x + 9} y={px(p).y - 8} fontSize={14} fontWeight={700} fill={INK}>
                {`${LETTERS[i]}′`}
              </HaloText>
            </g>
          ))}
          {wrong && (
            <g aria-hidden>
              <line
                x1={px(wrong.p).x - 7}
                y1={px(wrong.p).y - 7}
                x2={px(wrong.p).x + 7}
                y2={px(wrong.p).y + 7}
                stroke={C_BAD}
                strokeWidth={3}
              />
              <line
                x1={px(wrong.p).x - 7}
                y1={px(wrong.p).y + 7}
                x2={px(wrong.p).x + 7}
                y2={px(wrong.p).y - 7}
                stroke={C_BAD}
                strokeWidth={3}
              />
            </g>
          )}
          {/* sommets */}
          {pts.map((p, i) =>
            building ? (
              <g key={`v${i}`}>
                <circle cx={px(p).x} cy={px(p).y} r={i === k ? 7 : 5} fill={C_FIG} />
                <HaloText
                  x={px(p).x - 10}
                  y={px(p).y - 9}
                  textAnchor="middle"
                  fontSize={14}
                  fontWeight={700}
                  fill={INK}
                >
                  {LETTERS[i]}
                </HaloText>
              </g>
            ) : (
              <DragHandle
                key={`v${i}`}
                x={px(p).x}
                y={px(p).y}
                color={C_FIG}
                label={LETTERS[i]}
                labelDx={-12}
                labelDy={-12}
                name={`Sommet ${LETTERS[i]} ${ptText(p)}`}
                active={drag.active === `v${i}`}
                keyProps={drag.keyProps(`v${i}` as Handle)}
              />
            ),
          )}
          {/* poignées de l'outil */}
          {!building && kind === 'axial' && (
            <DragHandle
              x={px(axisHandle(q.axis)).x}
              y={px(axisHandle(q.axis)).y}
              color={C_TOOL}
              name="Axe de symétrie : le faire glisser"
              active={drag.active === 'axis'}
              keyProps={drag.keyProps('axis')}
            />
          )}
          {kind === 'central' &&
            (building ? (
              <circle cx={px(q.center).x} cy={px(q.center).y} r={5} fill={C_TOOL} />
            ) : (
              <DragHandle
                x={px(q.center).x}
                y={px(q.center).y}
                color={C_TOOL}
                label="O"
                labelDx={12}
                labelDy={12}
                name={`Centre O ${ptText(q.center)}`}
                active={drag.active === 'O'}
                keyProps={drag.keyProps('O')}
              />
            ))}
          {building && kind === 'central' && (
            <HaloText
              x={px(q.center).x + 12}
              y={px(q.center).y + 16}
              fontSize={14}
              fontWeight={700}
              fill={INK}
            >
              O
            </HaloText>
          )}
          {!building && kind === 'translation' && (
            <>
              <DragHandle
                x={px(q.from).x}
                y={px(q.from).y}
                color={C_TOOL}
                name={`Début de la flèche ${ptText(q.from)}`}
                active={drag.active === 'U'}
                keyProps={drag.keyProps('U')}
              />
              <DragHandle
                x={px(q.to).x}
                y={px(q.to).y}
                color={C_TOOL}
                name={`Pointe de la flèche ${ptText(q.to)}`}
                active={drag.active === 'V'}
                keyProps={drag.keyProps('V')}
              />
            </>
          )}
          {building && !done && (
            <g
              role="button"
              aria-label={`Curseur ${ptText(cursor)}. Flèches pour le déplacer, Entrée pour placer le point.`}
              className="outline-none [&:focus-visible_.drag-ring]:opacity-100"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  place(cursor);
                } else drag.keyProps('cursor').onKeyDown(e);
              }}
            >
              <circle
                className="drag-ring"
                cx={px(cursor).x}
                cy={px(cursor).y}
                r={14}
                fill="none"
                stroke="var(--color-primary-400)"
                strokeWidth={2.5}
                opacity={0}
              />
              <line
                x1={px(cursor).x - 9}
                y1={px(cursor).y}
                x2={px(cursor).x + 9}
                y2={px(cursor).y}
                stroke={INK}
                strokeWidth={1.5}
              />
              <line
                x1={px(cursor).x}
                y1={px(cursor).y - 9}
                x2={px(cursor).x}
                y2={px(cursor).y + 9}
                stroke={INK}
                strokeWidth={1.5}
              />
            </g>
          )}
        </svg>
      </div>

      {building ? (
        <div className="space-y-2" aria-live="polite">
          <p className="sr-only">{describe()}</p>
          {!done ? (
            <>
              <p className="text-ink-800 text-sm font-semibold" data-testid="tr-ask">
                {frenchSpaces(
                  `Touche le nœud où se trouve ${LETTERS[k]}′, l’image de ${LETTERS[k]}.`,
                )}
              </p>
              <button type="button" className={SECONDARY_BUTTON} onClick={() => place(cursor)}>
                {`Placer ${LETTERS[k]}′ au curseur`}
              </button>
              {wrong && (
                <Note live={false} tone="error">
                  <span data-testid="tr-wrong">{frenchSpaces(wrong.say)}</span>
                </Note>
              )}
            </>
          ) : (
            <Note live={false} tone="success">
              <span data-testid="tr-done">
                {frenchSpaces(`Bravo : les ${pts.length} images sont placées.`)}
              </span>
            </Note>
          )}
          <button type="button" className={PRIMARY_BUTTON} onClick={resetBuild}>
            Recommencer
          </button>
        </div>
      ) : (
        <>
          <ToggleChip on={construct} onClick={() => setConstruct((c) => !c)}>
            {`Construction de ${LETTERS[0]}′`}
          </ToggleChip>
          <Note live={false}>
            <span data-testid="tr-props">{frenchSpaces(properties(kind, rank))}</span>
            {kind === 'axial' && orientation(pts) !== orientation(images) && (
              <span className="mt-1 block">
                {frenchSpaces(
                  `Sens des sommets : ${pts.map((_, i) => LETTERS[i]).join(' → ')} tourne dans un sens, ${pts.map((_, i) => `${LETTERS[i]}′`).join(' → ')} dans l’autre.`,
                )}
              </span>
            )}
            <span className="mt-1 block">
              {frenchSpaces(
                `Aire de la figure : ${squares(area(pts))} ; aire de l’image : ${squares(area(images))}.`,
              )}
            </span>
          </Note>
        </>
      )}
      {outside && (
        <p className="text-ink-500 text-xs">
          {frenchSpaces(
            'Une partie de l’image sort du quadrillage : déplace l’axe, le centre ou la flèche.',
          )}
        </p>
      )}
    </div>
  );
}

/** comment on obtient A′ : perpendiculaire et distances égales, milieu O, flèche parallèle */
function Construction({
  kind,
  a,
  ai,
  b,
  bi,
  q,
  rank,
}: {
  kind: Kind;
  a: Pt;
  ai: Pt;
  b: Pt;
  bi: Pt;
  q: Params;
  rank: number;
}) {
  const A = px(a);
  const Ai = px(ai);
  if (kind === 'axial') {
    const h = px(footOnAxis(a, q.axis));
    const [s1, s2] = axisSegment(q.axis, 1);
    const ux = px(s2).x - px(s1).x;
    const uy = px(s2).y - px(s1).y;
    return (
      <g aria-hidden>
        <line
          x1={A.x}
          y1={A.y}
          x2={Ai.x}
          y2={Ai.y}
          stroke={INK}
          strokeWidth={1.5}
          strokeDasharray="5 4"
        />
        {!samePt(a, ai) && (
          <RightAngleMark x={h.x} y={h.y} ux={ux} uy={uy} vx={A.x - h.x} vy={A.y - h.y} />
        )}
        <Tick from={A} to={h} />
        <Tick from={h} to={Ai} />
      </g>
    );
  }
  if (kind === 'central') {
    const O = px(q.center);
    return (
      <g aria-hidden>
        <line
          x1={A.x}
          y1={A.y}
          x2={Ai.x}
          y2={Ai.y}
          stroke={INK}
          strokeWidth={1.5}
          strokeDasharray="5 4"
        />
        <Tick from={A} to={O} />
        <Tick from={O} to={Ai} />
      </g>
    );
  }
  const B = px(b);
  const Bi = px(bi);
  return (
    <g aria-hidden>
      <VectorArrow x1={A.x} y1={A.y} x2={Ai.x} y2={Ai.y} color={INK} width={2} dashed inset={5} />
      {rank >= 3 && (
        <polygon
          points={`${A.x},${A.y} ${Ai.x},${Ai.y} ${Bi.x},${Bi.y} ${B.x},${B.y}`}
          fill={C_TOOL}
          fillOpacity={0.12}
          stroke={C_TOOL}
          strokeWidth={1.5}
          strokeDasharray="5 4"
        />
      )}
    </g>
  );
}

/** petit trait de codage au milieu d'un segment (longueurs égales) */
function Tick({ from, to }: { from: { x: number; y: number }; to: { x: number; y: number } }) {
  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2;
  const d = Math.hypot(to.x - from.x, to.y - from.y) || 1;
  const nx = (-(to.y - from.y) / d) * 6;
  const ny = ((to.x - from.x) / d) * 6;
  return <line x1={mx - nx} y1={my - ny} x2={mx + nx} y2={my + ny} stroke={INK} strokeWidth={2} />;
}
